"use client";

import * as React from "react";
import { Eye, RefreshCcw } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";

import { useDebounce } from "@/src/hooks/useDebounce";
import { useApi } from "@/src/api/useApi";
import { useAuditLogsModal } from "../hooks/useAuditLogsModal";
import { AuditListType, DatumAuditListType } from "../types/audit";


/* ---------------- Utils ---------------- */
function toFaDate(input?: string | Date) {
  try {
    const d = input ? new Date(input) : new Date();
    return new Intl.DateTimeFormat("fa-IR", {
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    }).format(d);
  } catch {
    return "-";
  }
}

function safeJson(v: unknown) {
  try { return JSON.stringify(v, null, 2); } catch { return "—"; }
}

function shortPath(path: string) {
  if (!path) return "-";
  return path.length > 60 ? path.slice(0, 57) + "…" : path;
}

function methodFaLabel(m: DatumAuditListType["audit_history_log_http_method"]) {
  // هیچ انگلیسی به کاربر نشان نده
  switch (m) {
    case "GET": return "مشاهده";
    case "POST": return "ایجاد";
    case "PUT": return "ویرایش";
    case "PATCH": return "تغییر وضعیت";
    case "DELETE": return "حذف";
    default: return "عملیات";
  }
}

function methodBadgeVariant(m: DatumAuditListType["audit_history_log_http_method"]) {
  // فقط ظاهر
  if (m === "DELETE") return "destructive";
  if (m === "POST") return "secondary";
  if (m === "PATCH") return "outline";
  return "default";
}

function entityFaLabel(entity?: string | null) {
  // ترجمه حداقلی (هرچی لازم داشتی اضافه کن)
  const e = (entity || "").toLowerCase();
  if (e === "sales" || e === "orders") return "سفارش";
  if (e === "gallery" || e === "galleries") return "گالری";
  if (e === "articles") return "مقاله";
  if (e === "brands") return "برند";
  if (e === "categories") return "دسته‌بندی";
  if (e === "comments") return "نظر";
  if (e === "discounts") return "تخفیف";
  if (e === "productextras" || e === "productextra") return "افزودنی محصول";
  if (e === "supports" || e === "support") return "تیکت";
  if (e === "products") return "محصول";
  if (e === "productqas") return "سوالات متداول";
  if (e === "users") return "کاربر";
  if (!entity) return "—";
  return "مورد";
}

/* ---------------- Component ---------------- */
export default function AuditLogsModal() {
  const { state, close } = useAuditLogsModal();
  const filters = state.filters;

  const [page, setPage] = React.useState(1);
  const [q, setQ] = React.useState("");
  const dq = useDebounce(q, 350);

  const [selected, setSelected] = React.useState<DatumAuditListType | null>(null);
  const [detailsOpen, setDetailsOpen] = React.useState(false);

  // وقتی مدال باز میشه، صفحه و سرچ ریست بشه (ساده)
  React.useEffect(() => {
    if (state.isOpen) {
      setPage(1);
      setQ("");
      setSelected(null);
      setDetailsOpen(false);
    }
  }, [state.isOpen, filters]);

  const endpoint = React.useMemo(() => {
    if (!state.isOpen) return null; // وقتی مدال بسته است، درخواست نده
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("size", "20");
    if (dq) params.set("q", dq);

    if (filters?.parentEntity) params.set("parent_entity", filters.parentEntity);
    if (typeof filters?.parentId === "number") params.set("parent_id", String(filters.parentId));

    if (filters?.childEntity) params.set("child_entity", filters.childEntity);
    if (typeof filters?.childId === "number") params.set("child_id", String(filters.childId));

    if (filters?.controllerName) params.set("controller_name", filters.controllerName);

    // اگر خواستی فقط تغییر وضعیت‌ها (PATCH)
    if (filters?.onlyStatusChanges) params.set("method", "PATCH"); // باید تو بک‌اند پشتیبانی کنی

    return `/audit-logs?${params.toString()}`;
  }, [state.isOpen, page, dq, filters]);

  const { data, error, isLoading, refresh } = useApi<AuditListType>(endpoint);
  const payload = data?.data;
  const rows = payload?.data ?? [];

  const title = filters?.title || "گزارش تغییرات";

  return (
    <Dialog open={state.isOpen} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="min-w-7xl h-[82vh] overflow-hidden">
        <DialogHeader>
          <div className="flex items-center justify-between gap-2">
            <DialogTitle className="text-base sm:text-lg">{title}</DialogTitle>
            <Button variant="outline" size="sm" onClick={() => refresh()}>
              <RefreshCcw className="h-4 w-4 ml-2" />
              بروزرسانی
            </Button>
          </div>
        </DialogHeader>

        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="w-full sm:max-w-md">
            <Input
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(1); }}
              placeholder="جستجو…"
            />
          </div>
          <div className="text-sm text-muted-foreground">
            {payload ? `${payload.total.toLocaleString("fa-IR")} رکورد` : "—"}
          </div>
        </div>

        <Separator />

        <div className="flex-1 overflow-hidden">
          {error ? (
            <div className="border border-destructive rounded-md p-4">
              <p className="mb-2">خطا در دریافت گزارش.</p>
              <Button onClick={() => refresh()}>تلاش مجدد</Button>
            </div>
          ) : isLoading || !payload ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : (
            <>
              <div className="rounded-md border overflow-x-auto">
                <Table>
                  <TableHeader className="sticky top-0 z-10 bg-background">
                    <TableRow>
                      <TableHead className="text-center">زمان</TableHead>
                      <TableHead className="text-center">کاربر</TableHead>
                      <TableHead className="text-center">نوع عملیات</TableHead>
                      <TableHead className="text-center">مورد</TableHead>
                      <TableHead className="text-center">مسیر</TableHead>
                      <TableHead className="text-center">جزئیات</TableHead>
                    </TableRow>
                  </TableHeader>

                  <TableBody>
                    {rows.length ? rows.map((r) => {
                      const actor =
                        r.audit_history_log_actor_name ||
                        r.audit_history_log_actor_mobile ||
                        "—";

                      const entity =
                        r.audit_history_log_child_entity
                          ? `${entityFaLabel(r.audit_history_log_parent_entity)} → ${entityFaLabel(r.audit_history_log_child_entity)}`
                          : `${entityFaLabel(r.audit_history_log_parent_entity)}`;

                      const ids =
                        r.audit_history_log_child_id
                          ? `شناسه: ${r.audit_history_log_parent_id ?? "—"} / ${r.audit_history_log_child_id}`
                          : `شناسه: ${r.audit_history_log_parent_id ?? "—"}`;

                      return (
                        <TableRow key={r.audit_history_log_id} className="hover:bg-muted/40">
                          <TableCell className="text-center whitespace-nowrap">
                            {toFaDate(r.audit_history_log_created_at)}
                          </TableCell>

                          <TableCell className="text-center">
                            <div className="flex flex-col">
                              <span className="font-medium">{actor}</span>
                              <span className="text-xs text-muted-foreground">
                                {r.audit_history_log_success ? "موفق" : "ناموفق"}
                              </span>
                            </div>
                          </TableCell>

                          <TableCell className="text-center">
                            <Badge variant={methodBadgeVariant(r.audit_history_log_http_method)}>
                              {methodFaLabel(r.audit_history_log_http_method)}
                            </Badge>
                            <hr className="my-1" />
                            <p className="text-xs">{r.audit_history_log_action_name}</p>
                          </TableCell>

                          <TableCell className="text-center">
                            <div className="flex flex-col">
                              <span className="font-medium">{entity}</span>
                              <span className="text-xs text-muted-foreground">{ids}</span>
                            </div>
                          </TableCell>

                          <TableCell className="text-center">
                            <span className="text-xs font-mono">{shortPath(r.audit_history_log_http_path)}</span>
                          </TableCell>

                          <TableCell className="text-center">
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => {
                                setSelected(r);
                                setDetailsOpen(true);
                              }}
                            >
                              <Eye className="h-4 w-4 ml-2" />
                              نمایش کامل
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    }) : (
                      <TableRow>
                        <TableCell colSpan={6} className="h-24 text-center">
                          موردی یافت نشد.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>

              <div className="mt-3 flex items-center justify-between">
                <Button variant="outline" disabled={payload.page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>
                  قبلی
                </Button>
                <div className="text-sm text-muted-foreground">
                  صفحه {payload.page.toLocaleString("fa-IR")} از {payload.pages.toLocaleString("fa-IR")}
                </div>
                <Button variant="outline" disabled={payload.page >= payload.pages} onClick={() => setPage(p => p + 1)}>
                  بعدی
                </Button>
              </div>

              {/* جزئیات */}
              <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
                <DialogContent className="min-w-7xl h-[80vh] overflow-auto">
                  <DialogHeader>
                    <DialogTitle>جزئیات گزارش</DialogTitle>
                  </DialogHeader>

                  <ScrollArea className="h-[70vh] pr-2">
                    {selected ? (
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <Box label="زمان" value={toFaDate(selected.audit_history_log_created_at)} />
                          <Box label="نوع عملیات" value={methodFaLabel(selected.audit_history_log_http_method)} />
                          <Box label="مسیر" value={selected.audit_history_log_http_path} />
                          <Box label="وضعیت" value={selected.audit_history_log_success ? "موفق" : "ناموفق"} />
                        </div>

                        <Separator />

                        <Section title="تغییرات">
                          <pre className="text-xs rounded-md border p-3 bg-muted/30 overflow-auto">
                            {safeJson(selected.audit_history_log_diff || {})}
                          </pre>
                        </Section>

                        <Section title="قبل از تغییر">
                          <pre className="text-xs rounded-md border p-3 bg-muted/30 overflow-auto">
                            {safeJson(selected.audit_history_log_before || {})}
                          </pre>
                        </Section>

                        <Section title="بعد از تغییر">
                          <pre className="text-xs rounded-md border p-3 bg-muted/30 overflow-auto">
                            {safeJson(selected.audit_history_log_after || {})}
                          </pre>
                        </Section>
                      </div>
                    ) : (
                      <div className="text-muted-foreground">—</div>
                    )}
                  </ScrollArea>
                </DialogContent>
              </Dialog>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Box({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 text-sm font-medium break-words">{value}</div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <div className="text-sm font-bold">{title}</div>
      {children}
    </div>
  );
}
